import { z } from "zod";
import { Id, IsoDateTime, Page, PageQuery } from "./common";

// ---------------------------------------------------------------------------
// NetworkIdentity — first-class network and vendor identity records
//
// Distinct from internal deviceId (platform-owned) and ExternalIdMapping
// (integration-specific linkage). NetworkIdentity records capture the real-
// world hardware and network identifiers that operators and support teams
// need to correlate devices across systems.
// ---------------------------------------------------------------------------

/**
 * Identity namespaces. Each namespace defines the format and uniqueness rules
 * for values within it.
 *
 * Cellular:    IMEI (15 digits), ICCID (19-22 digits), IMSI (15 digits), EID (32 hex)
 * LoRaWAN:     DevEUI (16 hex), JoinEUI/AppEUI (16 hex)
 * Ethernet/Wi-Fi: MAC (12 hex, colon-separated)
 * Video/VMS:   ONVIF (UUID), VMS (vendor-defined string)
 * Stream:      StreamId (provider-assigned stream identifier)
 * Gateway:     GatewayId (gateway device's internal or external ID)
 * PKI:         CertFingerprint (SHA-256 hex of DER-encoded cert)
 * Other:       Catch-all for vendor-specific identifiers
 */
export const NetworkIdentityNamespace = z.enum([
  "IMEI",
  "ICCID",
  "IMSI",
  "EID",
  "DevEUI",
  "JoinEUI",
  "MAC",
  "ONVIF",
  "VMS",
  "StreamId",
  "GatewayId",
  "CertFingerprint",
  "Other",
]);
export type NetworkIdentityNamespace = z.infer<typeof NetworkIdentityNamespace>;

/**
 * Who provided this identity record.
 * Integration: sourced from an external integration via mapping or poll.
 * Manual: entered by an operator.
 * Provisioning: written during device onboarding.
 */
export const NetworkIdentitySource = z.enum([
  "Integration",
  "Manual",
  "Provisioning",
]);
export type NetworkIdentitySource = z.infer<typeof NetworkIdentitySource>;

/**
 * Controls which roles can view this identity field in the operator UI.
 * Internal: only Managers / TenantAdmins.
 * Operational: all authenticated users with devices:read.
 * Support: same as Operational but also surfaced in support/export workflows.
 */
export const NetworkIdentityVisibility = z.enum([
  "Internal",
  "Operational",
  "Support",
]);
export type NetworkIdentityVisibility = z.infer<typeof NetworkIdentityVisibility>;

export const NetworkIdentity = z.object({
  networkIdentityId: Id,
  tenantId: Id,
  deviceId: Id,
  namespace: NetworkIdentityNamespace,
  /** The raw identity value (IMEI digits, MAC address, hex fingerprint, etc.). */
  value: z.string().min(1).max(256),
  /** Optional operator-assigned label (e.g. "Primary SIM", "Backup ICCID"). */
  label: z.string().max(160).nullable(),
  source: NetworkIdentitySource,
  /** Integration that provided this identity. Null for Manual/Provisioning. */
  integrationId: Id.nullable(),
  /**
   * Whether this identity has been confirmed against the physical device.
   * False until verified by operator or provisioning workflow.
   */
  verified: z.boolean().default(false),
  visibility: NetworkIdentityVisibility.default("Operational"),
  /** Whether changes to this identity should write audit events. */
  auditable: z.boolean().default(true),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  createdBy: Id.nullable(),
  updatedBy: Id.nullable(),
});
export type NetworkIdentity = z.infer<typeof NetworkIdentity>;

export const NetworkIdentityCreateInput = z.object({
  namespace: NetworkIdentityNamespace,
  value: z.string().min(1).max(256),
  label: z.string().max(160).nullable().optional(),
  source: NetworkIdentitySource.default("Manual"),
  integrationId: Id.nullable().optional(),
  visibility: NetworkIdentityVisibility.optional(),
});
export type NetworkIdentityCreateInput = z.infer<typeof NetworkIdentityCreateInput>;

export const NetworkIdentityUpdateInput = z
  .object({
    label: z.string().max(160).nullable(),
    verified: z.boolean(),
    visibility: NetworkIdentityVisibility,
  })
  .partial();
export type NetworkIdentityUpdateInput = z.infer<typeof NetworkIdentityUpdateInput>;

export const NetworkIdentityListFilters = PageQuery.extend({
  namespace: NetworkIdentityNamespace.optional(),
  source: NetworkIdentitySource.optional(),
  verified: z.boolean().optional(),
});
export type NetworkIdentityListFilters = z.infer<typeof NetworkIdentityListFilters>;

export const NetworkIdentityListResponse = Page(NetworkIdentity);
export type NetworkIdentityListResponse = z.infer<typeof NetworkIdentityListResponse>;
